import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useContext,
  useState
} from "react";

import { ApiResponseProps, CartInstallmentsNumber, CartObject, CartPrice } from "@/types";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import { v4 as uuidv4 } from "uuid";

import { useAuth } from "@/contexts";

import {
  addProductRequest,
  deleteCartRequest,
  getCart,
  removeProductRequest,
  useBindClientCart
} from "@/hooks/Cart";

import { useChangeCartStep } from "@/hooks/Cart/useChangeCartStep";
import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { usePrevious } from "@chakra-ui/react";

type CartProviderProps = {
  children: ReactNode;
};

type CartContextData = {
  addProduct: (
    productId: string,
    ammount: number | string,
    id: string,
    wantDiscount?: boolean
  ) => Promise<ApiResponseProps>;
  updateCart: (id: string) => Promise<CartObject>;
  deleteCart: (id: string) => Promise<void>;
  removeProduct: (id: string, productId: string) => Promise<ApiResponseProps>;
  LinkClient: (cartId: string, clientId: string) => Promise<void>;
  changeCartPrices: Dispatch<SetStateAction<CartPrice[]>>;
  changeCartInstallmentsNumber: Dispatch<SetStateAction<number | CartInstallmentsNumber>>;
  cartStep: "CARRINHO" | "LOGIN" | "ENTREGA" | "PAGAMENTO" | "OBRIGADO";
  changeCartStep: (
    step: "CARRINHO" | "LOGIN" | "ENTREGA" | "PAGAMENTO" | "OBRIGADO"
  ) => Promise<void>;
  setFreightOption: Dispatch<SetStateAction<string>>;
  imperativeCartHandle: Dispatch<SetStateAction<CartObject>>;
  FreightOption: string;
  cart: CartObject;
  cartSize: number;
  checkoutId: string;
  cartInstallmentsNumber: CartInstallmentsNumber | number;
  ProductPageZipcode: string;
  rules?: string;
  setProductPageZipcode: Dispatch<SetStateAction<string>>;
  cartPrices: CartPrice[];
  cartOrderId: any;
  setCartOrderId: any;
  cepResumo: any;
  setCepResumo: any;
  resumoCep: any;
};

const CartContext = createContext<CartContextData>({} as CartContextData);

export function useCart(): CartContextData {
  const context = useContext(CartContext);
  return context;
}

function CartProviderComponent({ children }: CartProviderProps) {
  const { clientId } = useAuth();

  const STORAGEDCART = parseCookies();
  const STORE = process.env.NEXT_PUBLIC_STORE_NAME;
  const CHECKOUT_ID = process.env.NEXT_PUBLIC_CHECKOUT_ID;
  const CART_ITEMS = process.env.NEXT_PUBLIC_CART_ITEMS;
  const CART_EXPIRATION = process.env.NEXT_PUBLIC_CART_EXPIRATION;
  const [cart, setCart] = useState<CartObject>(null as CartObject);
  const [checkoutId, setCheckoutId] = useState(null);
  const [cartSize, setCartSize] = useState(0);
  const [cartOrderId, setCartOrderId] = useState("");
  const [cepResumo, setCepResumo] = useState(null);

  const [cartPrices, changeCartPrices] = useState<CartPrice[]>(null as CartPrice[]);
  const [cartInstallmentsNumber, changeCartInstallmentsNumber] = useState(null);
  const [ProductPageZipcode, setProductPageZipcode] = useState<string>(null);
  const previousInstallmentsNumber = usePrevious(cartInstallmentsNumber);
  const [cartStep, setCartStep] = useState<"CARRINHO" | "LOGIN" | "ENTREGA" | "PAGAMENTO">(
    "CARRINHO"
  );
  const [FreightOption, setFreightOption] = useState<string>(null);
  useIsomorphicLayoutEffect(() => {
    const CheckoutID = STORAGEDCART[CHECKOUT_ID];
    if (CheckoutID) {
      updateCart(CheckoutID);
      setCheckoutId(CheckoutID);
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    const FalsyTest = [undefined, null, false];
    if (
      FalsyTest.includes(cartInstallmentsNumber) &&
      !FalsyTest.includes(previousInstallmentsNumber)
    ) {
      changeCartInstallmentsNumber(previousInstallmentsNumber);
    }
  }, [cartInstallmentsNumber]);

  async function updateCart(id: string) {
    const { cartData } = await getCart(id);

    if (cartData) {
      setCart(cartData);

      const storeCart = JSON.stringify(cartData);

      if (cartData.items) {
        const size = await cartData.items.length;
        setCartSize(size);
      }

      setCookie(null, CART_ITEMS, storeCart, {
        maxAge: CART_EXPIRATION,
        path: "/"
      });

      return cartData;
    } else {
      setCart(null);
      setCartSize(0);
      return null;
    }
  }

  async function resumoCep(cep, CartShipping, total) {
    const onlyOneCep = CartShipping?.filter((shi) => shi.default === true);
    setCepResumo({ cep: cep, ship: onlyOneCep, total: total });
  }

  async function addProduct(productId: string, ammount: any, id: string, wantDiscount: boolean) {
    if (!id) {
      const newCheckoutId = uuidv4();
      id = newCheckoutId;
      setCheckoutId(newCheckoutId);
      setCookie(undefined, CHECKOUT_ID, newCheckoutId, {
        maxAge: CART_EXPIRATION,
        path: "/"
      });
    }

    try {
      const { data } = await addProductRequest(id, productId, ammount, STORE, wantDiscount);

      const status = data.status;
      const message = data.msgs;

      return await updateCart(id).then((Carto) => {
        return { status, message };
      });
    } catch (error) {
      console.error(error);
    }
  }

  async function removeProduct(id: string, productId: string) {
    try {
      const { data } = await removeProductRequest(id, productId);

      const status = data.status;
      const message = data.msgs;

      await updateCart(checkoutId);

      return { status, message };
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteCart(id: string) {
    const { data } = await deleteCartRequest(id);

    const status = data.status;
    const message = data.msgs;

    await destroyCookie(undefined, CART_ITEMS, {
      path: "/"
    });

    await destroyCookie(undefined, CHECKOUT_ID, {
      path: "/"
    });

    setCart(null);
    setCheckoutId(null);
    setCartSize(0);

    if (!status) {
      console.error(message);
    }
  }

  const LinkClient = async (cartId: string, clientId: string) => {
    const [ResponseBind, ErrorBind, LoadingBind] = await useBindClientCart(cartId, clientId);

    if (ResponseBind && ErrorBind === null) {
      updateCart(checkoutId);
    } else {
      console.log(ErrorBind);
    }
  };

  async function changeCartStep(Step: "CARRINHO" | "LOGIN" | "ENTREGA" | "PAGAMENTO") {
    const [SResponse, SError] = await useChangeCartStep(checkoutId, Step);
    if (SResponse && SError !== null) {
      setCartStep(Step);
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (clientId && checkoutId) {
      LinkClient(checkoutId, clientId);
    }
  }, [clientId]);

  return (
    <CartContext.Provider
      value={{
        cart,
        checkoutId,
        cartSize,
        cartInstallmentsNumber,
        cartPrices,
        FreightOption,
        cartOrderId,
        cepResumo,
        setCepResumo,
        resumoCep,
        imperativeCartHandle: setCart,
        setCartOrderId,
        LinkClient,
        updateCart,
        addProduct,
        removeProduct,
        deleteCart,
        cartStep,
        changeCartStep,
        changeCartInstallmentsNumber,
        changeCartPrices,
        setFreightOption,
        ProductPageZipcode,
        setProductPageZipcode
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

const CartProvider = memo(CartProviderComponent);

export { CartProvider };
